import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { CacheService } from '../../../services/cache.service';
import { catchError, map } from 'rxjs/operators';
import { StageCatalogEntry, StageTransition } from '../../../shared/stage-catalog';
import type { AssignableRep } from '../board/leads-board.component';
import {
  LEAD_STAGES_URL, LEAD_STAGE_TRANSITIONS_URL, ROLLOFF_VOCABULARY,
} from './rolloff-opportunity.model';

/** What every roll-off presentation needs before it can draw a hire. */
export interface RollOffContext {
  stages: StageCatalogEntry[];
  transitions: StageTransition[];
  reps: AssignableRep[];
}

/**
 * The stage vocabulary and the roster, fetched once for the whole page.
 *
 * IT USED TO LIVE ON THE BOARD, which was fine while the board was the only
 * way to see a hire. It is not any more: the Table shows the same hires and the
 * detail pane opens over either of them, so the vocabulary a hire's stage is
 * printed with cannot belong to one of the two views. A hire opened from a row
 * would have shown a stage id where a hire opened from a card shows a name.
 *
 * `shareReplay` rather than a fetch per subscriber: the board, the table and
 * the detail all ask on the same page load, and the answer is a tenant's
 * configuration - it does not change between three requests a second apart.
 *
 * FAILURES ARE ANSWERED, not thrown. A roster an account may not read leaves
 * the reps empty and owners named by id, exactly as the board has always done;
 * a stage catalog that fails is the one thing a caller has to be able to see,
 * so it comes back empty and the caller says so.
 */
@Injectable({ providedIn: 'root' })
export class RollOffContextService {

  constructor(private http: HttpClient, private cache: CacheService) {}

  /** The catalog and roster, fetched at most once per page load. */
  context(): Observable<RollOffContext> {
    return this.cache.load('rolloff', 'context', () => forkJoin({
      // Stages by vocabulary; edges unfiltered, because the endpoint returns
      // every declared edge and `legalMoveCodes` scopes them by the vocabulary
      // of the stage in hand. One request serves both boards.
      stages: this.http.get<StageCatalogEntry[]>(
        LEAD_STAGES_URL, { params: { vocabulary: ROLLOFF_VOCABULARY } })
        .pipe(catchError(() => of<StageCatalogEntry[]>([]))),
      transitions: this.http.get<StageTransition[]>(LEAD_STAGE_TRANSITIONS_URL)
        .pipe(catchError(() => of<StageTransition[]>([]))),
      // The lead pipeline's roster, because it is the same reps.
      reps: this.http.get<AssignableRep[]>('/api/sales-routes/reps')
        .pipe(catchError(() => of<AssignableRep[]>([]))),
    }).pipe(
      map(result => ({
        stages: result.stages ?? [],
        transitions: result.transitions ?? [],
        reps: result.reps ?? [],
      })),
    ));
  }

  /** A rep's name, or the id the row carries when the roster is unreadable.
   *  One spelling, so a hire does not read as two different people depending
   *  on which view is open. */
  static repLabel(reps: readonly AssignableRep[], userId: number | null): string {
    if (userId == null) return 'Unassigned';
    const rep = reps.find(candidate => candidate.userId === userId);
    return rep?.displayName?.trim() || rep?.userName?.trim() || `Rep ${userId}`;
  }
}
